import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import {
  MatchesResponse,
  MatchRespondRequest,
  MatchActionResponse,
  PhoneVerifySendRequest,
  PhoneVerifySendResponse,
  PhoneVerifyConfirmRequest,
  PhoneVerifyConfirmResponse,
} from './matching-api.models';

/**
 * Pure API service for matching HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class MatchingApiService {
  private readonly logger = inject(LoggerService).scope('MatchingApiService');

  private readonly apiBaseUrl = `${environment.API_BASE_URL}/protected`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /** GET /matches — Retrieve all match proposals grouped by status */
  public getMatches(): Observable<MatchesResponse> {
    return this.http.get<MatchesResponse>(`${this.apiBaseUrl}/matches`).pipe(
      catchError(error => {
        this.logger.error('Error fetching matches:', error);
        return throwError(() => new Error('Failed to fetch matches'));
      })
    );
  }

  /** POST /matches/{id}/respond — Accept or decline a match proposal */
  public respondToMatch(proposalId: string, body: MatchRespondRequest): Observable<MatchActionResponse> {
    return this.http.post<MatchActionResponse>(`${this.apiBaseUrl}/matches/${proposalId}/respond`, body).pipe(
      catchError(error => {
        this.logger.error('Error responding to match:', error);
        return throwError(() => new Error('Failed to respond to match'));
      })
    );
  }

  /** DELETE /matches/{id} — Cancel a match proposal */
  public cancelMatch(proposalId: string): Observable<MatchActionResponse> {
    return this.http.delete<MatchActionResponse>(`${this.apiBaseUrl}/matches/${proposalId}`).pipe(
      catchError(error => {
        this.logger.error('Error cancelling match:', error);
        return throwError(() => new Error('Failed to cancel match'));
      })
    );
  }

  /** POST /users/me/phone/verify/send — Send phone verification code */
  public sendPhoneVerification(body: PhoneVerifySendRequest): Observable<PhoneVerifySendResponse> {
    return this.http.post<PhoneVerifySendResponse>(`${this.apiBaseUrl}/users/me/phone/verify/send`, body).pipe(
      catchError(error => {
        this.logger.error('Error sending phone verification:', error);
        return throwError(() => error);  // Re-throw original error for 429 handling in effects
      })
    );
  }

  /** POST /users/me/phone/verify/confirm — Confirm phone verification code */
  public confirmPhoneVerification(body: PhoneVerifyConfirmRequest): Observable<PhoneVerifyConfirmResponse> {
    return this.http.post<PhoneVerifyConfirmResponse>(`${this.apiBaseUrl}/users/me/phone/verify/confirm`, body).pipe(
      catchError(error => {
        this.logger.error('Error confirming phone verification:', error);
        return throwError(() => error);  // Re-throw original error for status code handling
      })
    );
  }
}
